import React, { useState, useCallback } from 'react';
import { Popover, Box, Typography } from '@mui/material';

const GRID_ROWS = 8;
const GRID_COLS = 8;

/**
 * Hover grid to pick table dimensions; calls `onSelect` with 1-based rows/cols.
 *
 * @param {object} props - Component props.
 * @param {boolean} props.open - Whether the popover is visible.
 * @param {HTMLElement | null} props.anchorEl - Anchor element for the popover.
 * @param {() => void} props.onClose - Called when the popover should close.
 * @param {(rows: number, cols: number) => void} props.onSelect - Called with chosen row and column counts.
 * @returns {JSX.Element} MUI Popover with a hover grid.
 */
export function TableInsertGridPopover({ open, anchorEl, onClose, onSelect }) {
  const [hover, setHover] = useState({ rows: 0, cols: 0 });

  const handleCellEnter = useCallback((row, col) => {
    setHover({ rows: row + 1, cols: col + 1 });
  }, []);

  const handleGridLeave = useCallback(() => {
    setHover({ rows: 0, cols: 0 });
  }, []);

  const handleClick = useCallback(() => {
    if (hover.rows < 1 || hover.cols < 1) return;
    onSelect(hover.rows, hover.cols);
    setHover({ rows: 0, cols: 0 });
    onClose();
  }, [hover.rows, hover.cols, onSelect, onClose]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: { sx: { p: 1.5, maxWidth: 320 } },
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {hover.rows > 0 && hover.cols > 0
          ? `${hover.rows} × ${hover.cols}`
          : 'Insert table'}
      </Typography>
      <Box
        onMouseLeave={handleGridLeave}
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${GRID_COLS}, 16px)`,
          gap: '2px',
          width: 'fit-content',
        }}
      >
        {Array.from({ length: GRID_ROWS * GRID_COLS }).map((_, index) => {
          const row = Math.floor(index / GRID_COLS);
          const col = index % GRID_COLS;
          const active = row < hover.rows && col < hover.cols;
          return (
            <Box
              key={index}
              onMouseEnter={() => handleCellEnter(row, col)}
              onClick={handleClick}
              sx={{
                width: 14,
                height: 14,
                border: '1px solid',
                borderColor: active ? 'primary.main' : 'divider',
                bgcolor: active ? 'action.selected' : 'background.paper',
                cursor: 'pointer',
              }}
            />
          );
        })}
      </Box>
    </Popover>
  );
}
